<template>
  <div class="license-information py-3">
    <validation-observer ref="formValidator">
      <div class="body pt-4">
        <div class="container-fluid pdr-0 mgr-0">
          <div class="col ml-2 pdr-0 mgr-0 mb-2">
            <label class="data-label d-block">
              ¿La Licencia es Provisional?
            </label>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                :key="String(provisionalFlag?.value)"
                v-model="provisionalFlag"
                custom-error-msg="Favor de seleccionar si la licencia es provisional o no"
                :error="errors[0]"
                is-inline
                name="rbRecurrenceType"
                :options="provisionalOptions"
                @input="provisionalFlagChanged"
              />
            </validation-provider>
          </div>
          <div class="row mx-2">
            <div class="col-sm-12 col-md-3 mb-3">
              <validation-provider
                v-slot="{ errors }"
                name="LicenseType"
                :rules="{ required: true, notEmptyObject: 'id' }"
              >
                <custom-drop-down
                  v-model="selectedSpecialty"
                  custom-error-msg="Favor de seleccionar una Especialidad"
                  :disabled="license.specialistLicenseId != null"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Especialidad:"
                  :options="filteredSpecialtyTypes"
                  placeholder="Seleccione"
                  :reduce="(o) => ({ id: o.id, name: o.name })"
                  @input="onChangeSpecialtyHandler"
                />
              </validation-provider>
            </div>
            <div v-if="isPsychologist" class="col-sm-12 col-md-3">
              <label class="data-title mb-0">Nivel de Especialista:</label>
              <validation-provider
                v-slot="{ errors }"
                name="licenseSpecialtyType"
                rules="required"
              >
                <custom-drop-down
                  v-model="specialistLicense.specialtyLevel"
                  custom-error-msg="El nivel de especialista es requerido."
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  :options="levelTypes"
                  placeholder="Seleccione"
                />
              </validation-provider>
            </div>
            <div v-if="hasDetail" class="col-sm-12 col-md-3 mb-3">
              <validation-provider
                v-slot="{ errors }"
                name="LicenseNo"
                rules="required"
              >
                <custom-input
                  v-model="specialistLicense.licenseNo"
                  :custom-error-msg="validationMessages.licenseNo"
                  :disabled="cantEditLicenseNo"
                  :error="errors[0]"
                  label="Número de Licencia:"
                />
              </validation-provider>
            </div>
            <div v-if="hasDetail" class="col-sm-12 col-md-3 mb-3">
              <validation-provider
                v-slot="{ errors }"
                name="LicenseExpireDate"
                rules="required"
              >
                <custom-date-picker
                  v-model="specialistLicense.licenseExpireDate"
                  class="w-100"
                  :custom-error-msg="validationMessages.licenseExpireDate"
                  :error="errors[0]"
                  label="Fecha de Expiración:"
                  :min-date="new Date()"
                />
              </validation-provider>
            </div>
            <div v-if="hasDetail" class="col-sm-12 col-md-3 mb-3">
              <validation-provider
                v-slot="{ errors }"
                name="AgeRange"
                :rules="{ required: true, notEmptyObject: 'id' }"
              >
                <custom-drop-down
                  v-model="selectedAgeRange"
                  :custom-error-msg="validationMessages.ageRange"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Rango de Edad:"
                  :options="ageRanges"
                  placeholder="Seleccione"
                  :reduce="(o) => ({ id: o.id, name: o.name })"
                />
              </validation-provider>
            </div>
            <div class="col-sm-6 col-md mb-3">
              <check-box
                v-if="hasDetail && showSupervisorCheck"
                v-model="specialistLicense.supervisorFlag"
                class="checkbox"
                text="Supervisor"
              />
            </div>
          </div>

          <div v-if="isMedicaidDocumentationWorkflowEnabled" class="row mx-2">
            <div class="col-sm">
              <div class="mt-1">
                <custom-input
                  v-model="specialistLicense.medicalIdentifier"
                  :disabled="disabled"
                  label="Medicaid ID:"
                  maxlength="50"
                  placeholder="Ingrese el Medicaid ID"
                />
              </div>
            </div>
            <div class="col-sm">
              <custom-date-picker
                v-model="specialistLicense.medicaidDueDate"
                class-container="w-100"
                format="dd/MMM./yyyy"
                label="Fecha de Vencimiento Medicaid:"
                :min-date="currentDate"
              />
            </div>
          </div>

          <div
            v-if="hasSpecialistCertificationsEnable && isAdvisor"
            class="row ml-2 pdr-0 mgr-0"
          >
            <div class="col-12 text-muted">
              <span>Certificaciones:</span>
            </div>
            <div class="col-12 d-flex">
              <check-box
                v-model="specialistLicense.isCVE"
                class="checkbox"
                text="CVE - Certified Vocational Evaluator"
              />
              <check-box
                v-model="specialistLicense.isICVE"
                class="checkbox"
                text="ICVE - International Certified Vocational Evaluator"
              />
              <check-box
                v-model="specialistLicense.isCEV"
                class="checkbox"
                text="CEV - Certificado en Evaluación Vocacional"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="pl-4">
        <license-documents
          v-model="specialistLicense"
          @delete-document="deleteDocument"
          @download-document="downloadDocument"
        />
      </div>

      <error
        v-if="customErrorMessage != null"
        :message="customErrorMessage"
        variant="no-border"
      />
    </validation-observer>
    <div class="d-flex justify-content-end">
      <action-button
        class="py-2 btnCancel "
        i="fas fa-times-circle"
        text="Cancelar"
        variant="outline-danger"
        @click="cancel"
      />
      <action-button
        class="mx-3"
        i="fas fa-plus"
        text="Guardar"
        variant="success"
        @click="save"
      />
    </div>
  </div>
</template>

<script>
import CustomInput from '@/components/common/form/CustomInput';
import CustomDropDown from '@/components/common/form/CustomDropDown';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';
import LicenseDocuments from './LicenseDocuments';
import { serialize as objectToFormData } from 'object-to-formdata';
import CustomRadio from 'custom-radio';
import {
  getSpecialistServiceTypes,
  getSpecialistSpecialtyTypes,
  getSpecialistAgeRange,
  createLicense,
  editLicense,
  licenseCanSupervise,
  getSpecialistLicenseDocuments,
  deleteSpecialistLicenseDocument,
  getLevelTypes,
} from '@/modules/provider/api';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import generalService from '@/modules/app/api';
import Error from '@/components/common/Error';
import alert from '@/mixins/alert.mixin';
import { opMode } from '@/components/common/recurrence/utils/enums';

import specialistSpecialtyTypes from '@/utils/constants/specialistSpecialtyTypes.js';
import SpecialtyTypes from '@/utils/constants/specialtyTypes.js';

export default {
  name: 'LicenseInformationAddEdit',
  components: {
    Error,
    CustomInput,
    CustomDropDown,
    CustomDatePicker,
    LicenseDocuments,
    CustomRadio,
  },
  mixins: [alert],
  model: {
    prop: 'license',
    event: 'on-saved',
  },
  props: {
    license: {
      type: Object,
      default: () => ({
        id: null,
        licenseNo: null,
        licenseExpireDate: null,
        licenseTypeId: null,
        ageRange: null,
        ageRangeId: null,
        ageRangeName: null,
        isProvisional: false,
        isSupervisor: false,
        isCEV: null,
        isCVE: null,
        isICVE: null,
        documents: [],
        provisionalFlag: null,
        medicalIdentifier: null,
        medicaidDueDate: null,
      }),
    },
    isATEvaluator: {
      type: [Object, Boolean],
      default: () => false,
    },
    activateSpecialistLicenseFilterByHealthDepartment: {
      type: Boolean,
      default: false,
    },
    isRelatedMaster: {
      type: Boolean,
      default: false,
    },
    isCseeOfficial: {
      type: Boolean,
      default: false,
    },
    immediateSave: {
      type: Boolean,
      required: true,
    },
    //ECG:I = insert mode, E = edit mode, crear objeto congelado con esto?
    opMode: {
      type: String,
      default: () => '',
    },
    selectedLicenses: {
      type: Array,
      default: () => [],
    },
    customErrorMessage: {
      type: String,
      default: () => null,
    },
    specialistLicenseDocumentTypes: {
      type: Array,
      default: () => [],
    },
    specialistCompanyId: {
      type: Number,
      default: () => null,
    },
    isMedicaidDocumentationWorkflowEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      provisionalFlag: null,
      provisionalFlagIndex: 0,
      notProvisionalFlag: false,
      specialistLicense: { ...this.license },
      documents: [],
      specialtyTypes: [],
      ageRanges: [],
      selectedSpecialty: { id: 0, name: '' },
      selectedAgeRange: { id: 0, name: '' },
      validationMessages: {
        serviceType: 'Favor seleccionar un tipo servicio.',
        disciplineType: 'Favor seleccionar un servicio.',
        ageRange: 'Favor de seleccionar un Rango de Edad.',
        licenseNo: 'Favor de documentar un Número de Licencia.',
        licenseExpireDate: 'Favor de seleccionar una Fecha de Expiración.',
      },
      supervisorCheck: null,
      provisionalOptions: [
        { name: 'answerYes', label: 'Sí', value: true },
        { name: 'answerNo', label: 'No', value: false },
      ],
      hasSpecialistCertificationsEnable: false,
      levelTypes: [],
    };
  },
  computed: {
    currentDate() {
      return new Date();
    },
    isAdvisor() {
      return specialistSpecialtyTypes.Consejero === this.selectedSpecialty?.id;
    },
    fiscalYearId() {
      return this.$route.params.fiscalYearId;
    },
    companyId() {
      return this.specialistCompanyId ?? this.$route.params.companyId;
    },
    filteredSpecialtyTypes() {
      let filtered = this.specialtyTypes;

      if (this.isRelatedMaster) {
        filtered = filtered.filter((s) => s.id == 8); //8 Certificación de Asistencia Tecnológica
      }

      if (this.isCseeOfficial) {
        filtered = filtered.filter(
          (s) => s.id == specialistSpecialtyTypes.FuncionarioDEPR
        );
      } else {
        filtered = filtered.filter(
          (s) => s.id != specialistSpecialtyTypes.FuncionarioDEPR
        );
      }

      if (this.isATEvaluator.value) {
        filtered = filtered.filter(
          (s) => s.id !== specialistSpecialtyTypes.AsistenciaTecnologica
        );
      } else {
        filtered = filtered.filter(
          (s) => s.id !== specialistSpecialtyTypes.EvaluadorAutorizadoAT
        );
      }

      filtered = filtered.filter(
        (s) => !this.selectedLicenses.find((sl) => sl.specialtyTypeId == s.id)
      );
      if (
        this.activateSpecialistLicenseFilterByHealthDepartment &&
        !this.specialistLicense.provisionalFlag
      ) {
        filtered = filtered.filter((s) => !s.isFromHD);
      }
      return filtered;
    },
    hasDetail() {
      return (
        !this.selectedSpecialty ||
        (this.selectedSpecialty.id != SpecialtyTypes.ServicioEducativo &&
          this.selectedSpecialty.id != SpecialtyTypes.AsistenteServicio &&
          this.selectedSpecialty.id != SpecialtyTypes.FuncionarioDEPR)
      );
    },
    cantEditLicenseNo() {
      return !(
        this.opMode == opMode.INSERT ||
        (this.opMode == opMode.EDIT &&
          this.userHasPermissions('provider.specialist.edit.nolicense'))
      );
    },
    showSupervisorCheck() {
      return this.supervisorCheck;
    },
    isPsychologist() {
      return (
        this.specialistLicense.specialtyTypeId ===
          SpecialtyTypes.Psychologist &&
        this.isMedicaidDocumentationWorkflowEnabled
      );
    },
  },
  watch: {
    selectedSpecialty(val) {
      this.specialistLicense.specialtyTypeId = val.id;
      this.specialistLicense.specialtyTypeName = val.name;
    },
    selectedAgeRange(val) {
      this.specialistLicense.ageRangeCd = val.id;
      this.specialistLicense.ageRangeName = val.name;
    },
    specialistLicenseDocumentTypes: {
      immediate: true,
      handler() {
        this.refreshDocuments();
      },
    },
  },
  created() {
    getSpecialistServiceTypes().then((resp) => {
      this.serviceTypes = resp.data;
    });
    getSpecialistAgeRange().then((resp) => {
      this.ageRanges = resp.data;
    });
    getSpecialistSpecialtyTypes().then((resp) => {
      this.specialtyTypes = resp.data;
    });
    this.getLicenseDocuments();
    this.getParameter();
  },
  async mounted() {
    this.provisionalFlag = this.provisionalOptions.find(
      (x) => x.value === this.license.provisionalFlag
    );

    if (this.specialistLicense.specialtyTypeId) {
      this.selectedSpecialty = {
        id: this.specialistLicense.specialtyTypeId,
        name: this.specialistLicense.specialtyTypeName,
      };
      await this.isAllowedToSupervise(this.specialistLicense.specialtyTypeId);
    }
    if (this.specialistLicense.ageRangeCd) {
      this.selectedAgeRange = {
        id: this.specialistLicense.ageRangeCd,
        name: this.specialistLicense.ageRangeName,
      };
    }
    await this.getLevelTypes();
  },
  methods: {
    async getParameter() {
      const { data } = await _getSysparameterByNames({
        parameterNames: ['HasSpecialistCertificationsEnable'],
      });
      this.hasSpecialistCertificationsEnable =
        data?.length > 0 && data[0].staticValue?.toLowerCase() == 'true';
    },
    provisionalFlagChanged() {
      this.specialistLicense.provisionalFlag = this.provisionalFlag.value;
    },
    getLicenseDocuments() {
      getSpecialistLicenseDocuments(
        this.specialistLicense?.specialistLicenseId
      ).then((resp) => {
        this.documents = resp.data;
        this.refreshDocuments();
      });
    },
    async getLevelTypes() {
      const { data } = await getLevelTypes();
      this.levelTypes = data ?? [];
    },
    downloadDocument({ documentId, attachment }) {
      if (documentId) generalService.downloadDocumentByAttachmentId(documentId);
      else {
        let reader = new FileReader();
        reader.readAsDataURL(attachment);
        reader.onloadend = () => {
          let a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          const url = reader.result;
          a.href = url;
          a.download = attachment.name;
          a.click();
          window.URL.revokeObjectURL(url);
        };
      }
    },
    async deleteDocument(document) {
      if (document.specialistLicenseDocumentId > 0) {
        deleteSpecialistLicenseDocument(document.specialistLicenseDocumentId)
          .then(() => {
            this.ShowToast(
              'Proceso Exitoso',
              'Documento eliminado exitosamente.',
              'success'
            );
            this.getLicenseDocuments();
          })
          .catch((error) => {
            this.ShowToast('Error', error.response.data.message, 'error');
          });
        return;
      }
      document.attachment = null;
      document.documentFilename = null;
      document.guid = null;
      document.licenseDocumentExpiryDate = null;
    },
    refreshDocuments() {
      const documents = this.specialistLicenseDocumentTypes.map((x) => {
        const document = this.documents.find(
          (d) => d.specialistLicenseDocumentTypeId === x.id
        ); // to do, make sure this is the right document by its type
        if (document) {
          document.licenseDocumentExpiryDate = document.expirationDate
            ? new Date(document.expirationDate)
            : null;
          return document;
        }
        return {
          specialistLicenseDocumentId: null,
          specialistLicenseId: null,
          specialistLicenseDocumentTypeId: x.id,
          specialistLicenseDocumentTypeName: x.name,
          documentId: null,
          attachment: null,
          licenseDocumentExpiryDate: null,
          guid: null,
        };
      });
      this.specialistLicense.documents = documents;
    },
    async save() {
      var self = this;

      const isValid = await this.$refs.formValidator.validate();

      if (isValid) {
        if (self.immediateSave) {
          let promise = null;
          const documentsPayload = this.specialistLicense.documents
            .filter((doc) => Boolean(doc.attachment))
            .map((doc) => ({
              ...doc,
              expirationDate: doc?.licenseDocumentExpiryDate
                ? doc?.licenseDocumentExpiryDate?.toISOString()?.split('T')[0]
                : null,
            }));

          const payload = {
            ...self.specialistLicense,
            documents: documentsPayload,
            companyId: this.companyId,
            fiscalYearId: this.fiscalYearId,
          };

          const params = objectToFormData(
            { ...payload },
            {
              indices: true,
              nullsAsUndefineds: true,
              dotsForObjectNotation: true,
            }
          );

          if (self.opMode == opMode.EDIT) {
            promise = editLicense(params);
          } else if (self.opMode == opMode.INSERT) {
            promise = createLicense(params);
          }

          await promise
            .then((res) => {
              self.ShowToast(
                'Proceso Exitoso',
                'Licencia guardada exitosamente.',
                'success'
              );
              self.specialistLicense = res.data;
              self.$emit('on-saved', self.specialistLicense);
            })
            .catch((error) => {
              self.ShowToast('Error', error.response.data.message, 'error');
            });
        } else {
          self.$emit('on-saved', self.specialistLicense);
        }
      } else {
        //TODO: Use the stored one
        self.ShowToast('Error', 'Información incompleta. (190803)', 'error');
      }
    },
    async cancel() {
      this.$emit('on-cancel');
    },
    async isAllowedToSupervise(specialtyId) {
      licenseCanSupervise(specialtyId).then((resp) => {
        this.supervisorCheck = resp.data;
      });
    },
    async onChangeSpecialtyHandler(specialty) {
      if (!this.isPsychologist) {
        this.specialistLicense.specialtyLevel = null;
      }
      await this.isAllowedToSupervise(specialty.id);
    },
  },
};
</script>
<style scoped>
@media (min-width: 576px) {
  .license-information .btnCancel {
    float: left;
  }
  .actions {
    padding-left: 35px;
  }
}

@media (min-width: 768px) {
  .license-information .btnCancel {
    float: inherit;
  }
}

@media (min-width: 992px) {
}

@media (min-width: 1200px) {
}

.checkbox {
  margin-top: 33px !important;
  margin-left: 35%;
}
@media (max-width: 768px) {
  .checkbox {
    margin-top: 15px !important;
    margin-left: 20%;
  }
}

.input-group.checkbox {
  margin-left: 0px;
  margin-top: 3px !important;
  margin-bottom: 18px;
}

::v-deep .custom-datetime-picker-container {
  width: 100% !important;
}
</style>
